// SEO-12 · /resources/school-staff-logins-and-access
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Claims registry D3 (approved wording): a directory entry is NOT a login; access is a separate
//     recorded grant that links the identities; leavers are DEACTIVATED, not deleted; employee codes
//     come from one per-school counter; PAN/UAN/ESIC/IFSC are validated server-side; bank values are
//     protected and masked. "Access is granted deliberately, and never as a side effect." Never
//     imply self-registered staff logins.
//   • CLAUDE.md: minting is centralised in `core/staff_access.provision_login` — created ACTIVE with
//     a UNIQUE RANDOM temp password (never a shared default) plus must_change_password, the I-04
//     identity link, and a handover bundle; there is no staff self-signup.
//   • Permissions resolve FRESH per request (the JWT carries only `sub`), so a role change takes
//     effect immediately; record-level teacher scoping limits a classroom persona to its assigned
//     (class, section); mandatory password→OTP 2FA with cumulative attempt caps, plus TOTP.
//   • Separation of duties on money/approvals (refunds, concessions, leave, payroll) — CLAUDE.md
//     "Security (non-negotiable)".
// NO invented breach statistics, no certification claim.

const article = {
  slug: "school-staff-logins-and-access",
  title: "Access is granted, never inherited: staff logins in a school",
  seoTitle: "School Staff Logins and Access Control | Vidyapeeth360",
  description:
    "Why a staff record should not be a login, what a leaver's account needs to keep, and how to make access changes take effect the moment you make them.",
  topic: "Staff & operations",
  topicKey: "operations",
  readingMinutes: 5,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "The account that causes trouble in a school is almost never a hacker's. It is the part-time coach from two years ago whose login still opens the student directory, because nobody was ever asked to close it.",
  moduleLinks: ["role-and-permission", "staff-management", "user-management"],
  conversion: {
    to: "/pricing",
    label: "See plans and the full cost schedule",
    note: "Roles, permissions and two-factor sign-in are part of the core product in every plan, not a security add-on.",
  },
  sections: [
    {
      id: "record-vs-login",
      heading: "A staff record is not a login",
      blocks: [
        {
          t: "p",
          text: "The convenient design is for creating an employee to create an account. It is also the design that quietly gives system access to the visiting music teacher, the contractor, the bus attendant and the person who joined last week and starts on Monday.",
        },
        {
          t: "p",
          text: "Keep the two apart. A directory entry says this person works here. A login says this person can open the system, and that should be a separate act somebody performed, on a date, for a reason. When the two are the same event, nobody can answer the only question that matters at audit: who can currently sign in, and who decided that?",
        },
      ],
    },
    {
      id: "handover",
      heading: "Handing over the first password",
      blocks: [
        {
          t: "p",
          text: "The moment an account is created is the moment most school systems become insecure, because the first password is usually a pattern: the school's name, the year, or the same default for everyone.",
        },
        {
          t: "ol",
          items: [
            "**Generate it, uniquely, per person.** A shared default is a master key that spreads by word of mouth and never expires.",
            "**Force a change on first sign-in.** The temporary password is a handover mechanism, not a credential.",
            "**Hand it over deliberately.** Give it to the person, in a form you are willing to have recorded; do not print a list of them for the staff room noticeboard.",
            "**Never send credentials through a channel you do not control**, and never in the same message as the sign-in link if you can avoid it.",
            "**Require a second factor.** For anyone who can see student data or move money, a password alone is not a boundary — it is a single string that has probably been reused.",
          ],
        },
      ],
    },
    {
      id: "leavers",
      heading: "Deactivate; do not delete",
      blocks: [
        {
          t: "p",
          text: "When a teacher leaves, the instinct is to delete the account and tidy up. Do not. Their name is on marks they entered, invoices they raised, approvals they gave and messages they sent. Deleting the account either orphans that history or, worse, lets it be reassigned.",
        },
        {
          t: "p",
          text: "Deactivate instead: the account can no longer sign in, and every record it touched still names who touched it. If the same person returns next session, reactivating is a decision somebody makes rather than a new identity that fragments their history.",
        },
        {
          t: "note",
          title: "The quarterly question",
          text: "Once a term, print the list of accounts that can sign in and read it against the current staff list. It takes about twenty minutes, needs no tooling, and any account nobody in the room can account for is the finding.",
        },
      ],
    },
    {
      id: "scope",
      heading: "Roles are the floor; record scope is the ceiling",
      blocks: [
        {
          t: "p",
          text: "\"Teacher\" is a role. It is not an answer to the question of which students a teacher may see. In a school of two thousand, a role that grants the whole student directory to everyone teaching a subject is a role that has not been thought about.",
        },
        {
          t: "ul",
          items: [
            "**Scope by assignment.** A class teacher works with their own class; a subject teacher with the sections they actually teach. Derive that from the timetable and the teaching assignments rather than asking somebody to maintain a second list.",
            "**Separate duties on anything reversible in money.** The person who requests a refund, a waiver or a leave approval should not be the person who approves it.",
            "**Make changes take effect now.** If a permission change only applies after the next sign-in, then revoking access from somebody at 11 a.m. does not actually revoke it. Resolve permissions per request, not once per session.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "There is no staff self-signup. An employee record is a record; access is granted explicitly, through one shared path that creates the login, links it to the person's identity, generates a unique random temporary password, marks it must-change, and returns a handover bundle the administrator gives to the individual. Re-granting to somebody who already has a login links the existing account rather than creating a second one.",
        },
        {
          t: "p",
          text: "Sign-in is password plus a one-time code by default, with an authenticator app and single-use backup codes available; wrong-code attempts are capped cumulatively rather than reset by asking for a new code. Permissions are resolved fresh on every request — the session token carries an identity, not a permission list — so a role change or a revocation applies to the next click, not the next login.",
        },
        {
          t: "p",
          text: "Beyond roles, a pure classroom persona is scoped to the class-sections they are actually assigned, derived from the timetable and explicit teaching assignments, so a subject teacher's marks and attendance surfaces stop at their own sections. Separation of duties is enforced on refunds, concessions, leave approvals and payroll — the requester cannot be the approver.",
        },
        {
          t: "p",
          text: "Leavers are deactivated with their history intact, employee codes come from one per-school series, and statutory identifiers are format-checked on the server rather than trusted from the form. The [access model](/modules/role-and-permission), the [staff register](/modules/staff-management) and the [user list](/modules/user-management) are three views of the same decision: who is here, and what may they open.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
